import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  getAuth,
  onAuthStateChanged,
} from "firebase/auth";

import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  collection
} from "firebase/firestore";

import {
  ref,
  deleteObject,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

import {
  Trash2,
  Save,
  ArrowLeft,
  Upload,
  Loader2,
  CheckCircle,
  XCircle,
  Send,
  X,
} from "lucide-react";

import { db, storage } from "../web/src/firebaseClient";

import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";

export default function ITKStarterProjectDetailPage() {
  const { id } = useParams();

  const navigate = useNavigate();

  const auth = getAuth();

  const [approveModalOpen, setApproveModalOpen] =
  useState(false);

  const [approveMessage, setApproveMessage] =
  useState("");

  const [project, setProject] = useState(null);
  const [form, setForm] = useState(null);

  const [currentUser, setCurrentUser] = useState(null);

  const [isAdmin, setIsAdmin] = useState(false);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [imageUploading, setImageUploading] =
    useState(false);

  const [moderationLoading, setModerationLoading] =
    useState(false);

  const [rejectModalOpen, setRejectModalOpen] =
    useState(false);

  const [rejectMessage, setRejectMessage] =
    useState("");

  /* AUTH */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        setCurrentUser(user || null);

        if (!user) {
          setIsAdmin(false);
          return;
        }

        try {
          const token =
            await user.getIdTokenResult(true);

          setIsAdmin(
            token.claims.role === "admin" ||
              token.claims.admin === true
          );
        } catch (err) {
          console.error(err);

          setIsAdmin(false);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  /* FETCH PROJECT */
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const refDoc = doc(db, "starterApplications", id);

        const snap = await getDoc(refDoc);

        if (!snap.exists()) {
          setProject(null);
          setForm(null);
        } else {
          const data = {
            id: snap.id,
            ...snap.data(),
          };

          setProject(data);

          setForm(data);

          setRejectMessage(
  `Merhaba ${data.teamLeader || ""},

  İTK Starter başvurunuzu değerlendirdik.

  Bu aşamada başvurunuzun programa dahil edilmesine devam edemiyoruz.

  Başvurunuz ve emeğiniz için teşekkür ederiz.

  Sevgiler,
  İTK Girişimcilik ve İnovasyon Ekibi`
          );

          setApproveMessage(
  `Merhaba ${data.teamLeader || ""},

  İTK Starter başvurunuz değerlendirme sürecini başarıyla tamamladı.

  Başvurunuz programa kabul edilmiştir.

  Kısa süre içerisinde sizinle iletişime geçeceğiz.

  Tebrik ederiz.

  Sevgiler,
  İTK Girişimcilik ve İnovasyon Ekibi`
          );
        }

      } catch (error) {
        console.error(error);

      } finally {
        setLoading(false);
      }
    };

    fetchProject();

  }, [id]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />

        <div className="p-6">Yükleniyor...</div>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />

        <div className="p-6">
          Proje bulunamadı.
        </div>
      </main>
    );
  }

  const isOwner =
    currentUser &&
    project.uid === currentUser.uid;

  /* INPUT CHANGE */
  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  /* IMAGE UPDATE */
  const handleImageUpdate = async (e) => {
    const file = e.target.files?.[0];

    if (!file || !isOwner) return;

    try {
      setImageUploading(true);

      if (project.imagePath) {
        try {
          await deleteObject(
            ref(storage, project.imagePath)
          );
        } catch (err) {
          console.warn(
            "Eski görsel silinemedi:",
            err
          );
        }
      }

      const imagePath =
        `starterApplications/${currentUser.uid}/${id}/cover-${Date.now()}.jpg`;

      const imageRef = ref(storage, imagePath);

      await uploadBytes(imageRef, file, {
        contentType:
          file.type || "image/jpeg",
      });

      const imageUrl =
        await getDownloadURL(imageRef);

      await updateDoc(
        doc(db, "starterApplications", id),
        {
          imageUrl,
          imagePath,
        }
      );

      setProject((prev) => ({
        ...prev,
        imageUrl,
        imagePath,
      }));

      setForm((prev) => ({
        ...prev,
        imageUrl,
        imagePath,
      }));
    } catch (error) {
      console.error(error);

      alert("Görsel güncellenemedi.");
    } finally {
      setImageUploading(false);
    }
  };

  /* UPDATE */
  const handleUpdate = async () => {
    if (!isOwner) return;

    try {
      setSaving(true);

      await updateDoc(
        doc(db, "starterApplications", id),
        {
          projectName: form.projectName,
          category: form.category,
          teamLeader: form.teamLeader,
          teamMembers: form.teamMembers,
          email: form.email,
          targetAudience:
            form.targetAudience,
          problem: form.problem,
          solution: form.solution,
          difference: form.difference,
        }
      );

      alert("Proje güncellendi.");
    } catch (error) {
      console.error(error);

      alert(
        "Güncelleme sırasında hata oluştu."
      );
    } finally {
      setSaving(false);
    }
  };

  /* DELETE */
  const handleDelete = async () => {
    if (!isOwner) return;

    const ok = window.confirm(
      "Bu projeyi silmek istediğine emin misin?"
    );

    if (!ok) return;

    try {
      if (project.imagePath) {
        try {
          await deleteObject(
            ref(storage, project.imagePath)
          );
        } catch (e) {
          console.warn(
            "Görsel silinemedi:",
            e
          );
        }
      }

      await deleteDoc(
        doc(db, "starterApplications", id)
      );

      alert("Proje silindi.");

      navigate("/itk-starter");
    } catch (error) {
      console.error(error);

      alert(
        error.message ||
          "Silme sırasında hata oluştu."
      );
    }
  };

  /* APPROVE */
  const handleApprove = async () => {
    if (!isAdmin || !currentUser) return;

    if (!project.email) {
      alert("Başvuru sahibinin email adresi bulunamadı.");
      return;
    }

    if (!approveMessage.trim()) {
      alert("Lütfen onay mesajını girin.");
      return;
    }

    try {
      setModerationLoading(true);

      await updateDoc(
        doc(db, "starterApplications", id),
        {
          status: "approved",
          isApproved: true,
          approvalMessage: approveMessage.trim(),
          moderatedBy: currentUser.uid,
          moderatedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      );

      await addDoc(collection(db, "mail"), {
        to: [project.email],

        message: {
          subject: "İTK Starter Başvurunuz Onaylandı",

          text: approveMessage.trim(),
        },
      });

      alert(
        "Başvuru onaylandı ve mail gönderim kuyruğuna eklendi."
      );

      setApproveModalOpen(false);

    } catch (error) {
      console.error(error);

      alert(
        error.message ||
        "Başvuru onaylanırken hata oluştu."
      );

    } finally {
      setModerationLoading(false);
    }
  };

  /* REJECT */
  const handleRejectAndOpenMail = async () => {
    if (!isAdmin || !currentUser) return;

    if (!project.email) {
      alert("Başvuru sahibinin email adresi bulunamadı.");
      return;
    }

    if (!rejectMessage.trim()) {
      alert("Lütfen ret mesajını girin.");
      return;
    }

    try {
      setModerationLoading(true);

      await updateDoc(doc(db, "starterApplications", id), {
        status: "rejected",
        isApproved: false,
        rejectionMessage: rejectMessage.trim(),
        moderatedBy: currentUser.uid,
        moderatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "mail"), {
        to: project.email,
        message: {
          subject: "İTK Starter Başvurunuz Hakkında",
          text: rejectMessage.trim(),
        },
      });

      alert("Başvuru reddedildi ve mail gönderim kuyruğuna eklendi.");

      setRejectModalOpen(false);
      navigate("/admin/moderation");
    } catch (error) {
      console.error("Reject/mail error:", error);
      alert(error.message || "Başvuru reddedilirken veya mail gönderilirken hata oluştu.");
    } finally {
      setModerationLoading(false);
    }
  };

  return (
    <main
      className="min-h-screen bg-[#eef2f7]"
      style={{
        fontFamily: "Poppins",
      }}
    >
      <InnovationHubHeader />

      <section className="px-4 py-6">
        <div className="mx-auto max-w-[1100px]">

          <button
            onClick={() => navigate(-1)}
            className="mb-4 flex items-center gap-2 text-sm text-[#173b7a]"
          >
            <ArrowLeft size={16} />
            Geri
          </button>

          <div className="rounded-2xl bg-white p-6 shadow">

            {/* IMAGE */}
            <div className="relative mb-5 aspect-video overflow-hidden rounded-xl bg-gray-100">

              {project.imageUrl && (
                <img
                  src={project.imageUrl}
                  className="h-full w-full object-cover"
                />
              )}

              {imageUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">

                  <Loader2
                    className="animate-spin text-white"
                    size={32}
                  />
                </div>
              )}

              {isOwner && (
                <label className="absolute bottom-3 right-3 flex cursor-pointer gap-2 rounded-xl bg-[#1565C0] px-3 py-2 text-sm text-white shadow transition hover:bg-[#0f57aa]">

                  <Upload size={16} />

                  Görsel değiştir

                  <input
                    type="file"
                    onChange={
                      handleImageUpdate
                    }
                    className="hidden"
                  />
                </label>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">

              <Input
                label="Proje adı"
                name="projectName"
                value={
                  form.projectName || ""
                }
                onChange={handleChange}
                disabled={!isOwner}
              />

              <Input
                label="Kategori"
                name="category"
                value={
                  form.category || ""
                }
                onChange={handleChange}
                disabled={!isOwner}
              />

              <Input
                label="Ekip lideri"
                name="teamLeader"
                value={
                  form.teamLeader || ""
                }
                onChange={handleChange}
                disabled={!isOwner}
              />

              <Input
                label="Email"
                name="email"
                value={form.email || ""}
                onChange={handleChange}
                disabled={!isOwner}
              />

              <Input
                label="Hedef kitle"
                name="targetAudience"
                value={
                  form.targetAudience ||
                  ""
                }
                onChange={handleChange}
                disabled={!isOwner}
              />
            </div>

            <Textarea
              label="Problem"
              name="problem"
              value={form.problem || ""}
              onChange={handleChange}
              disabled={!isOwner}
            />

            <Textarea
              label="Çözüm"
              name="solution"
              value={form.solution || ""}
              onChange={handleChange}
              disabled={!isOwner}
            />

            <Textarea
              label="Fark"
              name="difference"
              value={
                form.difference || ""
              }
              onChange={handleChange}
              disabled={!isOwner}
            />

            <Textarea
              label="Ekip üyeleri"
              name="teamMembers"
              value={
                form.teamMembers || ""
              }
              onChange={handleChange}
              disabled={!isOwner}
            />

            {/* BUTTONS */}
            {(isOwner || isAdmin) && (
              <div className="mt-6 flex flex-wrap justify-end gap-2">

                {isOwner && (
                  <>
                    <button
                      onClick={
                        handleUpdate
                      }
                      disabled={saving}
                      className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {saving ? (
                        <Loader2
                          className="animate-spin"
                          size={14}
                        />
                      ) : (
                        <Save size={14} />
                      )}

                      Güncelle
                    </button>

                    <button
                      onClick={
                        handleDelete
                      }
                      className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
                    >
                      <Trash2
                        size={14}
                      />
                      Sil
                    </button>
                  </>
                )}

                {isAdmin && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        setRejectModalOpen(
                          true
                        )
                      }
                      disabled={
                        moderationLoading
                      }
                      className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-60"
                    >
                      <XCircle
                        size={14}
                      />

                      Reddet
                    </button>

                   <button
                     type="button"
                     onClick={() => setApproveModalOpen(true)}
                     disabled={moderationLoading}
                     className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-60"
                   >
                   <CheckCircle size={14} />
                    Onayla
                   </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* REJECT MODAL */}
      {rejectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">

          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">

            <div className="mb-4 flex items-start justify-between gap-4">

              <div>
                <h3 className="text-xl font-semibold text-[#173b7a]">
                  Ret Maili
                </h3>

                <p className="mt-1 text-sm text-gray-500">
                  Mail içeriğini düzenleyebilirsiniz.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setRejectModalOpen(
                    false
                  )
                }
                className="rounded-lg bg-gray-100 p-2 text-gray-500 hover:bg-gray-200"
              >
                <X size={18} />
              </button>
            </div>

            <textarea
              value={rejectMessage}
              onChange={(e) =>
                setRejectMessage(
                  e.target.value
                )
              }
              rows={12}
              className="w-full resize-none rounded-xl border border-gray-200 p-4 text-sm leading-relaxed outline-none focus:border-[#1565C0]"
            />

            <div className="mt-4 flex justify-end gap-2">

              <button
                type="button"
                onClick={() =>
                  setRejectModalOpen(
                    false
                  )
                }
                className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-200"
              >
                Vazgeç
              </button>

            <button
              type="button"
              onClick={handleRejectAndOpenMail}
              disabled={moderationLoading}
              className="flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {moderationLoading ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Send size={16} />
              )}

              Gönder
            </button>
            </div>
          </div>
        </div>
      )}
      {approveModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-[#173b7a]">
                  Onay Maili
                </h3>

                <p className="mt-1 text-sm text-gray-500">
                  Mail içeriğini düzenleyebilirsiniz.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setApproveModalOpen(false)}
                className="rounded-lg bg-gray-100 p-2 text-gray-500 hover:bg-gray-200"
              >
                <X size={18} />
              </button>
            </div>

            <textarea
              value={approveMessage}
              onChange={(e) => setApproveMessage(e.target.value)}
              rows={12}
              className="w-full resize-none rounded-xl border border-gray-200 p-4 text-sm leading-relaxed outline-none focus:border-[#1565C0]"
            />

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setApproveModalOpen(false)}
                className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-200"
              >
                Vazgeç
              </button>

              <button
                type="button"
                onClick={handleApprove}
                disabled={moderationLoading}
                className="flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
              >
                {moderationLoading ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <Send size={16} />
                )}
                Gönder
              </button>
            </div>
          </div>
        </div>
      )}

      <InnovationHubFooter />
    </main>
  );
}

/* INPUT */
function Input({
  label,
  ...props
}) {
  return (
    <label className="block">

      <span className="text-sm text-[#173b7a]">
        {label}
      </span>

      <input
        {...props}
        className="mt-1 w-full rounded-xl border p-3"
      />
    </label>
  );
}

/* TEXTAREA */
function Textarea({
  label,
  value,
  onChange,
  ...props
}) {
  const textareaRef = React.useRef(null);

  useEffect(() => {
    const textarea = textareaRef.current;

    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value]);

  const handleTextareaChange = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = `${e.target.scrollHeight}px`;

    if (onChange) {
      onChange(e);
    }
  };

  return (
    <label className="mt-4 block">
      <span className="text-sm text-[#173b7a]">
        {label}
      </span>

      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleTextareaChange}
        {...props}
        rows={1}
        className="mt-1 w-full resize-none overflow-hidden rounded-xl border p-3 leading-relaxed"
      />
    </label>
  );
}